import { ColumnConfig } from '../field-renderer/field-config.interface';

/**
 * Message templates for empty states and selection banners.
 *
 * @example
 * ```ts
 * const messages: DataTableMessages = {
 *   noSearch: 'Enter search criteria to find Airports',
 *   noMatch: 'No Airports match your search',
 *   allPageSelected: (entity, count) => `All ${count} ${entity} on this page are selected.`,
 *   selectAll: (entity, total) => `Select all ${total} ${entity}`,
 * };
 * ```
 */
export interface DataTableMessages {
  /** Shown when no search has been performed yet */
  noSearch: string;
  /** Shown when search returns zero results */
  noMatch: string;
  /** Banner text when current page is fully selected. Receives entity name and selected count. */
  allPageSelected: (itemType: string, count: number) => string;
  /** Link text to select all items across pages. Receives entity name and total count. */
  selectAll: (itemType: string, total: number) => string;
}

/**
 * Feature flags to enable/disable table capabilities.
 * All flags default to `false` if not specified.
 *
 * @example
 * ```ts
 * const features: DataTableFeatures = {
 *   selection: true,           // Row checkboxes
 *   pagination: true,          // Page controls
 *   sorting: true,             // Column sort headers
 *   columnReorder: true,       // Drag-drop columns
 *   selectAllAcrossPages: true, // "Select all N items" banner
 *   columnCustomization: true   // Column customization menu (more_vert icon)
 * };
 * ```
 */
export interface DataTableFeatures {
  /** Enable row selection checkboxes */
  selection?: boolean;
  /** Single-row radio selection (no header control). Requires `selection: true`. Defaults to checkbox multi-select when omitted. */
  singleSelection?: boolean;
  /** Show paginator below table */
  pagination?: boolean;
  /** Enable column sorting */
  sorting?: boolean;
  /** Allow drag-drop column reordering */
  columnReorder?: boolean;
  /** Show "Select all N items" banner for cross-page selection */
  selectAllAcrossPages?: boolean;
  /** Show column customization menu (more_vert icon) */
  columnCustomization?: boolean;
  /** Suppress default empty-state message (use custom empty UI in parent) */
  hideEmptyMessage?: boolean;
  /** Emit rowClick on any cell click, not just hyperlink cells */
  rowClickOnAllCells?: boolean;
  /** Reserved for print functionality */
  print?: boolean;
  /** Reserved for download functionality */
  download?: boolean;
}

/**
 * Default pagination settings applied on first load.
 *
 * @template TRow - Row type for type-safe sortField
 */
export interface DataTablePagination<TRow = any> {
  /** Items per page (default 10) */
  pageSize: number;
  /** Initial sort column key */
  sortField: keyof TRow;
  /** Initial sort direction */
  sortDirection: 'asc' | 'desc';
}

/**
 * Main configuration object for DataTableComponent.
 *
 * @template TRow - The row data type for type safety
 *
 * @example
 * ```ts
 * interface Airport {
 *   id: number;
 *   airportCode: string;
 *   name: string;
 * }
 *
 * const config: DataTableConfig<Airport> = {
 *   rowIdField: 'id',  // Unique identifier for selection
 *   entityName: 'Airports',
 *   storageKey: 'sm-airports',  // localStorage key for column preferences
 *   columns: [
 *     { key: 'airportCode', label: 'Code', sortable: true, isHyperlink: true },
 *     { key: 'name', label: 'Name', sortable: true },
 *   ],
 *   features: {
 *     selection: true,
 *     pagination: true,
 *     sorting: true,
 *   },
 *   messages: {
 *     noSearch: 'Search for airports',
 *     noMatch: 'No airports found',
 *     allPageSelected: (e, c) => `All ${c} ${e} selected.`,
 *     selectAll: (e, t) => `Select all ${t} ${e}`,
 *   },
 *   defaultPagination: {
 *     pageSize: 10,
 *     sortField: 'airportCode',
 *     sortDirection: 'desc',
 *   },
 * };
 * ```
 */
export interface DataTableConfig<TRow = any> {
  /**
   * Property name used as unique row identifier.
   * Critical for selection tracking across pages.
   */
  rowIdField: keyof TRow;

  /** Display name for messages (e.g., "Airports", "Users") */
  entityName: string;

  /** Column definitions. See ColumnConfig for options. */
  columns: ColumnConfig<TRow>[];

  /** Feature toggles */
  features: DataTableFeatures;

  /** Empty state and selection banner messages */
  messages: DataTableMessages;

  /** localStorage key for persisting column visibility/order */
  storageKey: string;

  /**
   * Customize-grid checkbox that cannot be hidden (summary-table `defaultSelectedColumn`).
   * Defaults to the first `isHyperlink` column, else the first column key.
   */
  defaultLockedColumn?: keyof TRow | string;

  /** Initial pagination state (optional) */
  defaultPagination?: DataTablePagination<TRow>;

  /** When set, row checkboxes are disabled for rows where this returns true. */
  rowSelectionDisabledFn?: (row: TRow) => boolean;
}
